import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  CarInfo,
  CarInfoPayload,
  getCarInfo,
  submitConsign,
} from '@apps/config/API/consign.config';
import {
  Alert,
  Dialog,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import consignSuccessImage from '@stack/assets/images/consignment/consign_success.png';
import SuccessIcon from '@stack/assets/images/consignment/success.svg';

import { ConsignInner } from '../styles';

import { ConsignFormProps, ConsignFormValues } from './props';
import {
  FormBand,
  FormFields,
  FormHeader,
  FormItem,
  FormWrapper,
  PlaceholderText,
  SubmitButton,
  SuccessBody,
  SuccessCaption,
  SuccessIconWrapper,
  SuccessImage,
  SuccessTitle,
} from './styles';

const DEFAULT_VALUES: ConsignFormValues = {
  user_name: '',
  user_mobile: '',
  user_state: 0,
  car_brand: 0,
  car_model: 0,
  manufactured_year: 0,
  car_variant: 0,
};

const EMPTY_CAR_INFO: CarInfo = {
  brand_list: [],
  model_list: [],
  year_list: [],
  variant_list: [],
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const ConsignForm: React.FC<ConsignFormProps> = (props) => {
  const { webView, locationState = [] } = props;

  // ======================= HOOKS
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm<ConsignFormValues>({
    mode: 'onBlur',
    defaultValues: DEFAULT_VALUES,
  });

  // ======================= STATE
  const [carInfo, setCarInfo] = useState<CarInfo>(EMPTY_CAR_INFO);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const loadCarInfo = useCallback(async (payload: CarInfoPayload) => {
    const result = await getCarInfo(payload);

    if (result.ok) {
      setCarInfo(result.data);
    }
  }, []);

  // ======================= EFFECTS
  useEffect(() => {
    loadCarInfo({});
  }, [loadCarInfo]);

  // ======================= EVENTS
  /**
   * Picking a level clears every level below it, then refetches the lists.
   * The payload is rebuilt from the surviving values so a stale downstream id is never sent.
   */
  const onSelectLevel = (
    level: 'brand' | 'model' | 'year' | 'variant',
    value: number
  ) => {
    const payload: CarInfoPayload = {};

    if (level === 'brand') {
      setValue('car_model', 0);
      setValue('manufactured_year', 0);
      setValue('car_variant', 0);
      payload.car_brand = value;
    }

    if (level === 'model') {
      setValue('manufactured_year', 0);
      setValue('car_variant', 0);
      payload.car_brand = getValues('car_brand');
      payload.car_model = value;
    }

    if (level === 'year') {
      setValue('car_variant', 0);
      payload.car_brand = getValues('car_brand');
      payload.car_model = getValues('car_model');
      payload.manufactured_year = value;
    }

    if (level === 'variant') {
      payload.car_brand = getValues('car_brand');
      payload.car_model = getValues('car_model');
      payload.manufactured_year = getValues('manufactured_year');
      payload.car_variant = value;
    }

    loadCarInfo(payload);
  };

  const onHandleSubmit = async (values: ConsignFormValues) => {
    setSubmitError(null);
    setIsSubmitting(true);

    const result = await submitConsign({
      consign_name: values.user_name,
      consign_mobile: values.user_mobile,
      consign_brand: values.car_brand,
      consign_model: values.car_model,
      consign_variant: values.car_variant,
      consign_year: values.manufactured_year,
      consign_state: values.user_state,
      consign_color: undefined,
    });

    setIsSubmitting(false);

    if (!result.ok) {
      setSubmitError(result.error);
      return;
    }

    setIsSuccessOpen(true);
    reset(DEFAULT_VALUES);
    loadCarInfo({});
  };

  const onCloseSuccess = () => setIsSuccessOpen(false);

  // ======================= VARIABLES
  const selectProps = {
    displayEmpty: true,
    fullWidth: true,
  };

  // ======================= RENDER FUNCTIONS
  const renderSelect = ({
    name,
    placeholder,
    requiredMessage,
    level,
    options,
    disabled,
  }: {
    name:
      | 'user_state'
      | 'car_brand'
      | 'car_model'
      | 'manufactured_year'
      | 'car_variant';
    placeholder: string;
    requiredMessage: string;
    /** Omitted for selects outside the brand -> model -> year -> variant cascade. */
    level?: 'brand' | 'model' | 'year' | 'variant';
    options: { label: string; value: number }[];
    disabled?: boolean;
  }) => (
    <FormItem>
      <Controller
        name={name}
        control={control}
        rules={{ validate: (value) => !!value || requiredMessage }}
        render={({ field: { onChange, value, onBlur } }) => (
          <FormControl fullWidth>
            <Select
              {...selectProps}
              value={value || 0}
              disabled={disabled}
              onBlur={onBlur}
              onChange={(event) => {
                const nextValue = Number(event.target.value);
                onChange(nextValue);

                if (level) {
                  onSelectLevel(level, nextValue);
                }
              }}
            >
              <MenuItem value={0} disabled style={{ display: 'none' }}>
                <PlaceholderText>{placeholder}</PlaceholderText>
              </MenuItem>
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
      <FormHelperText error>{errors[name]?.message}</FormHelperText>
    </FormItem>
  );

  // ======================= VIEWS
  return (
    <FormBand>
      <ConsignInner>
        <FormWrapper $fullWidth={webView}>
          <form onSubmit={handleSubmit(onHandleSubmit)}>
            <FormHeader>
              I want to consign my car! Trade-in price too low.
            </FormHeader>

            <FormFields>
              <FormItem>
                <Controller
                  name="user_name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Your Full Name"
                      error={!!errors.user_name}
                    />
                  )}
                />
                <FormHelperText error>
                  {errors.user_name?.message}
                </FormHelperText>
              </FormItem>

              <FormItem>
                <Controller
                  name="user_mobile"
                  control={control}
                  rules={{ required: 'Mobile No. is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Contact Number"
                      error={!!errors.user_mobile}
                    />
                  )}
                />
                <FormHelperText error>
                  {errors.user_mobile?.message}
                </FormHelperText>
              </FormItem>

              {renderSelect({
                name: 'user_state',
                placeholder: 'Select State',
                requiredMessage: 'State is required',
                options: locationState.map((state) => ({
                  label: state.state_name,
                  value: state.state_id,
                })),
              })}

              {renderSelect({
                name: 'car_brand',
                placeholder: 'Car Brand',
                requiredMessage: 'Car Brand is required',
                level: 'brand',
                options: carInfo.brand_list.map((brand) => ({
                  label: brand.car_brand_name,
                  value: brand.car_brand_id,
                })),
              })}

              {renderSelect({
                name: 'car_model',
                placeholder: 'Car Model',
                requiredMessage: 'Model is required',
                level: 'model',
                disabled: !watch('car_brand'),
                options: carInfo.model_list.map((model) => ({
                  label: model.car_model_name,
                  value: model.car_model_id,
                })),
              })}

              {renderSelect({
                name: 'manufactured_year',
                placeholder: 'Select Manufactured Year',
                requiredMessage: 'Year is required',
                level: 'year',
                disabled: !watch('car_model'),
                options: carInfo.year_list.map((year) => ({
                  label: `${year.year}`,
                  value: year.year,
                })),
              })}

              {renderSelect({
                name: 'car_variant',
                placeholder: 'Car Variant',
                requiredMessage: 'Variant is required',
                level: 'variant',
                disabled: !watch('manufactured_year'),
                options: carInfo.variant_list.map((variant) => ({
                  label: variant.car_variant_name,
                  value: variant.car_variant_id,
                })),
              })}
            </FormFields>

            {submitError && (
              <Alert severity="error" sx={{ marginBottom: '16px' }}>
                {submitError}
              </Alert>
            )}

            <SubmitButton type="submit" $loading={isSubmitting}>
              {isSubmitting ? 'Checking' : 'Get Your Free Valuation'}
            </SubmitButton>
          </form>
        </FormWrapper>
      </ConsignInner>

      <Dialog
        open={isSuccessOpen}
        onClose={onCloseSuccess}
        maxWidth={false}
        PaperProps={{
          sx: { width: '550px', maxWidth: '550px', margin: '20px' },
        }}
      >
        <SuccessImage src={consignSuccessImage.src} alt="" />
        <SuccessBody>
          <SuccessIconWrapper>
            <SuccessIcon />
          </SuccessIconWrapper>
          <SuccessTitle>
            Consignment Request Successfully Submitted.
          </SuccessTitle>
          <SuccessCaption>
            The consignment value is provided for estimation purposes only. It
            is not final or accurate, as the vehicle has not yet undergone a
            professional inspection by our team. In addition, the market value
            and consignment value may fluctuate over time, depending on
            prevailing market conditions.
          </SuccessCaption>
        </SuccessBody>
      </Dialog>
    </FormBand>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export * from './props';
export default ConsignForm;
